import {
  formatQueryRes,
  useAppSelector,
  useGetHaInstanceExtraInfoQuery,
  useUpgradeMutation
} from '@monorepo/shared';
import { selectCurrentAccount } from '@monorepo/shared/store/accountSlice';
import { selectAccountAllInstances } from '@monorepo/shared/store/haInstanceSlice';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  ButtonToolbar,
  DatePicker,
  Dropdown,
  Loader,
  Message,
  Radio,
  RadioGroup,
  Stack
} from 'rsuite';
import './Upgrade.less';
import {
  getDefaultDate,
  getEtaMins,
  getSuggestedTimeStr,
  isDisabledDate,
  isDisabledHour,
  isDisabledMinute
} from '../../utils/schedule';

export const Upgrade = () => {
  // const now = new Date('2024-10-31T23:04:00');
  const navigate = useNavigate();
  const { instanceId, accountId } = useParams();
  const [instanceUpgrade, upgradeResult] = useUpgradeMutation();
  const accountRegionInstances = useAppSelector(selectAccountAllInstances);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const [instance, setInstance] = useState<Record<string, any>>({});
  const [region, setRegion] = useState<Record<string, any>>({});
  const [isCheck, setIsCheck] = useState<boolean>(false);
  const [isScheduled, setIsScheduled] = useState<boolean>(false);
  const [value, setValue] = useState<string>('suggested');
  const [selectedDate, setSelectedDate] = useState<Date>(getDefaultDate());
  const [selectedVersion, setSelectedVersion] = useState<Record<string, any>>(
    {}
  );
  const [showMessage, setShowMessage] = useState<boolean>(false);
  const [message, setMessage] = useState<Record<string, any>>({
    type: '',
    content: '',
    header: ''
  });
  const handleChange = (d: Date) => {
    setSelectedDate(d);
  };
  const submit = () => {
    const etaMins = getEtaMins(selectedDate);
    if (etaMins < 0) {
      setMessage({
        type: 'error',
        header: 'A problem occurred:',
        content: 'Select time not correct!'
      });
      setShowMessage(true);
    } else {
      setShowMessage(false);
      instanceUpgrade({
        account_id: accountId,
        user_id: currentAccount.user_id,
        access_from_user_id:
          currentAccount.access_from_user_id ||
          currentAccount.access_from?.user_id,
        instance_id: instanceId,
        region: region,
        to_version: selectedVersion?.to_version,
        etaMins
      });
    }
  };
  const {
    data: instanceExtraInfo,
    isLoading: instanceExtraInfoLoading,
    isFetching: instanceExtraInfoFetching
  } = useGetHaInstanceExtraInfoQuery(
    formatQueryRes({
      account: currentAccount,
      instance_id: instanceId,
      region
    }),
    { skip: !region.token }
  );

  useEffect(() => {
    const versionUpgrade = instanceExtraInfo?.version_upgrade;
    if (versionUpgrade) {
      const scheduledTime = versionUpgrade?.scheduled_time;
      const createTime = versionUpgrade?.create_time;
      const versionRoutes = versionUpgrade?.routes;
      if (scheduledTime) {
        setIsScheduled(true);
        setMessage({
          type: 'success',
          header: 'Firmware Upgrade Scheduled!',
          content: `Upgrade scheduled at: ${scheduledTime}`
        });
        setShowMessage(true);
      } else if (versionRoutes?.length) {
        setSelectedVersion(versionRoutes[0]);
      }
    }
  }, [instanceExtraInfo]);

  useEffect(() => {
    const accountData = accountRegionInstances?.[accountId || ''];
    if (accountData && instanceId) {
      const inst = accountData.find(
        (obj: Record<string, any>) => obj.instance_id == instanceId
      );
      inst && setInstance({ ...inst });
      inst?.region && setRegion(inst.region);
    }
  }, [accountRegionInstances, instanceId]);

  useEffect(() => {
    if (value === 'now') {
      setSelectedDate(new Date());
    } else if (value === 'suggested') {
      setSelectedDate(getDefaultDate());
    }
  }, [value]);

  useEffect(() => {
    if (upgradeResult.error) {
      const data: Record<string, any> = upgradeResult.error;
      const code = data?.error_code || data?.status;
      const messageText = data?.error_desc || data?.message;
      if (messageText) {
        setMessage({
          type: 'error',
          header: 'A problem occurred:',
          content: `${messageText} [Error_code: ${code}]`
        });
        setShowMessage(true);
      }
    } else if (upgradeResult.data) {
      const data: Record<string, any> = upgradeResult.data;
      if (data?.keep_check) {
        setIsCheck(true);
      } else if (data?.state || data?.scheduled_time) {
        setMessage({
          ...message,
          header: data?.state,
          content: data?.scheduled_time
            ? `Upgrade scheduled at${data?.scheduled_time}`
            : '',
          type: 'info'
        });
        setShowMessage(true);
        setIsScheduled(true);
      } else {
        setShowMessage(false);
      }
    }
  }, [upgradeResult]);
  return (
    <div className="upgrade-content" style={{ height: '100%' }}>
      {upgradeResult.isLoading ? (
        <div style={{ height: '90%', position: 'relative' }}>
          <Loader center size="md" />
        </div>
      ) : (
        <>
          {showMessage && (
            <Message
              showIcon
              type={message?.type}
              header={message?.header}
              style={{ marginBottom: '20px' }}
            >
              <ul>{message?.content && <li>{message?.content}</li>}</ul>
            </Message>
          )}
          {!isScheduled && !isCheck ? (
            <>
              <Stack
                direction="column"
                style={{ marginBottom: '25px' }}
                spacing={20}
                alignItems="stretch"
              >
                <Stack.Item>OS Version</Stack.Item>
                <Stack.Item grow={1}>
                  <Dropdown
                    appearance="default"
                    className="upgrade-dropdown"
                    style={{ width: '100%' }}
                    size="md"
                    title={selectedVersion?.to_version}
                  >
                    {instanceExtraInfo?.version_upgrade?.routes.map(
                      (obj: Record<string, any>, idx: number) => (
                        <Dropdown.Item
                          key={idx}
                          onSelect={(e) => setSelectedVersion(obj)}
                          active={
                            selectedVersion?.to_version === obj?.to_version
                          }
                        >
                          {obj?.to_version}
                        </Dropdown.Item>
                      )
                    )}
                  </Dropdown>
                </Stack.Item>
              </Stack>
              <RadioGroup
                name="radio-group-controlled"
                value={value}
                onChange={(v: any) => setValue(`${v}`)}
              >
                <Radio value="suggested">
                  Suggested Upgrade time: Today at {getSuggestedTimeStr()}
                </Radio>
                <Radio value="scheduled">Select a time</Radio>
                <DatePicker
                  value={selectedDate}
                  onChange={(d: any) => handleChange(d)}
                  disabled={value !== 'scheduled'}
                  placeholder={'Schedule upgrade'}
                  format="yyyy-MM-dd HH:mm"
                  size="md"
                  shouldDisableDate={(d) => isDisabledDate(d)}
                  shouldDisableHour={(h, d) => isDisabledHour(h, d)}
                  shouldDisableMinute={(m, d) => isDisabledMinute(m, d)}
                  block
                  // placement="auto"
                  placement="bottomStart"
                />
                <Radio value="now">Upgrade now</Radio>
              </RadioGroup>
              <ButtonToolbar
                style={{ justifyContent: 'left', marginTop: '10%' }}
              >
                <Button
                  appearance="ghost"
                  className="action-btn"
                  onClick={() => {
                    navigate(-1);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  // size="sm"
                  onClick={() => {
                    submit();
                  }}
                  className="action-btn"
                  appearance="primary"
                >
                  {'Confirm'}
                </Button>
              </ButtonToolbar>
            </>
          ) : (
            <ButtonToolbar style={{ justifyContent: 'left', marginTop: '10%' }}>
              <Button
                appearance="ghost"
                className="action-btn"
                onClick={() => {
                  navigate(-1);
                }}
              >
                Go Back
              </Button>
            </ButtonToolbar>
          )}
        </>
      )}
    </div>
  );
};
